export interface ApiValidationDetail {
  path: string[];
  message: string;
}

export interface ApiErrorPayload {
  message?: string;
  details?: ApiValidationDetail[];
  status?: number;
}

type ApiErrorResponse = {
  message?: string;
  details?: ApiValidationDetail[];
  error?: string;
};

function parseResponseData(data: unknown): ApiErrorResponse | null {
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as ApiErrorResponse;
    } catch {
      return { message: data };
    }
  }
  if (typeof data === 'object') {
    return data as ApiErrorResponse;
  }
  return null;
}

/**
 * Normalizes Axios / fetch-style errors into a consistent payload shape.
 */
export function extractApiErrorPayload(error: unknown): ApiErrorPayload {
  const anyError = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };

  const data = parseResponseData(anyError?.response?.data);
  const status = anyError?.response?.status;

  if (!data) {
    const networkMessage = typeof anyError?.message === 'string' ? anyError.message.trim() : '';
    return networkMessage ? { message: networkMessage, status } : { status };
  }

  const message =
    (typeof data.message === 'string' ? data.message.trim() : undefined) ||
    (typeof data.error === 'string' ? data.error.trim() : undefined);
  const details = Array.isArray(data.details) ? data.details : undefined;

  return { message, details, status };
}

/**
 * Extract user-facing error message from an API error (e.g. Axios 4xx/5xx response).
 * Prefers response.data.message; falls back to first detail message for validation payloads.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const { message, details } = extractApiErrorPayload(error);

  if (message && message !== 'Validation failed') {
    return message;
  }

  const firstDetail = details?.[0]?.message;
  if (typeof firstDetail === 'string' && firstDetail.trim()) {
    return firstDetail.trim();
  }

  if (message) {
    return message;
  }

  return fallback;
}

/**
 * Parse validation details from API error response for use with Ant Design form.setFields().
 */
export function getApiValidationDetails(
  error: unknown
): Array<{ name: string[]; errors: string[] }> {
  const { details } = extractApiErrorPayload(error);
  if (!details?.length) return [];

  return details.map((d) => ({
    name: d.path?.length ? d.path.map(String) : ['unknown'],
    errors: [typeof d.message === 'string' && d.message.trim() ? d.message : 'Invalid value']
  }));
}

/**
 * HTTP status of an Axios / fetch-style error, if the request reached the server.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  return extractApiErrorPayload(error).status;
}

/** Field name → error messages, as returned by Django REST Framework 400 responses. */
export type ApiFieldErrors = Record<string, string[]>;

/**
 * Extracts DRF field errors (`{ field: ['msg', …] }`) from an API error.
 * Keys listed in `nestedKeys` hold nested serializer errors and are flattened into the result.
 * Returns null when the body carries no field errors.
 */
export function getDrfFieldErrors(error: unknown, nestedKeys: string[] = []): ApiFieldErrors | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const fieldErrors: ApiFieldErrors = {};
  const assign = (name: string, value: unknown) => {
    if (!Array.isArray(value)) return;
    const messages = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    if (messages.length > 0) fieldErrors[name] = messages;
  };

  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (nestedKeys.includes(key) && value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
        assign(childKey, childValue);
      }
      continue;
    }
    assign(key, value);
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}

/**
 * Converts DRF field errors into the shape Ant Design `form.setFields()` expects.
 */
export function toFormFieldErrors(fieldErrors: ApiFieldErrors): Array<{ name: string; errors: string[] }> {
  return Object.entries(fieldErrors).map(([name, messages]) => ({
    name,
    errors: Array.isArray(messages) ? messages : [String(messages)],
  }));
}
